import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import React from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Option } from 'types/Generics';
import styles from './ExpandableSelector.module.scss';

interface ExpandableSelectorProps<T extends string | number | readonly string[]> {
    label?: string | React.ReactNode;
    value?: T;
    options: Array<Option<T>>;
    className?: string;
    defaultFocusId?: T;
    onChange?: (value: Option<T>) => void;
}

export const ExpandableSelector = <T extends string | number | readonly string[]>({
    label,
    value,
    options,
    className,
    defaultFocusId,
    onChange
}: ExpandableSelectorProps<T>) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [showAbove, setShowAbove] = React.useState(false);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const itemRefs = React.useRef<Map<T, HTMLDivElement>>(new Map());
    const selectedOption = options.find(option => option.id === value);

    React.useEffect(() => {
        if (isOpen && buttonRef.current && containerRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const containerHeight = containerRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - buttonRect.bottom;
            const spaceAbove = buttonRect.top;

            // If not enough space below but more space above, show above
            if (spaceBelow < containerHeight && spaceAbove > spaceBelow) {
                setShowAbove(true);
            } else {
                setShowAbove(false);
            }

            // Scroll to the selected value if it exists, otherwise to the default focus
            const scrollToId = value ?? defaultFocusId;
            if (scrollToId && itemRefs.current.has(scrollToId)) {
                const itemElement = itemRefs.current.get(scrollToId);
                if (itemElement && containerRef.current) {
                    itemElement.scrollIntoView({
                        block: 'start'
                    });
                }
            }
        }
    }, [isOpen, value, defaultFocusId]);

    const handleOptionClick = (option: Option<T>) => {
        onChange?.(option);
        setIsOpen(false);
    };

    return (
        <div className={clsx(styles.root, className)}>
            <span className={styles.label}>{selectedOption ? selectedOption.name : label}</span>

            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <div>
                    <button
                        ref={buttonRef}
                        className={styles.button}
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <FontAwesomeIcon
                            className={styles.icon}
                            icon={isOpen ? faChevronUp : faChevronDown}
                        />
                    </button>

                    {isOpen && (
                        <div
                            ref={containerRef}
                            className={clsx(styles.container, {
                                [styles.containerAbove]: showAbove
                            })}
                        >
                            {options.map((option, index) => (
                                <div
                                    key={index}
                                    ref={el => {
                                        if (el) {
                                            itemRefs.current.set(option.id, el);
                                        } else {
                                            itemRefs.current.delete(option.id);
                                        }
                                    }}
                                    className={clsx(styles.item, {
                                        [styles.selected]: option.id === value
                                    })}
                                    onClick={() => handleOptionClick(option)}
                                >
                                    {option.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default ExpandableSelector;
